/**
 * Utility functions for comparing content and identifying differences
 */

/**
 * Compare two strings and return the differences with markings for additions
 * This uses a word-based comparison approach with improved accuracy
 * @param {string} original - Original content
 * @param {string} improved - Improved content
 * @returns {object} Object containing segments with their highlight status
 */
export const compareContent = (original, improved) => {
  if (!original || !improved) {
    return {
      segments: [{ text: improved || '', isAdded: true }],
      hasChanges: original !== improved
    };
  }

  // Tokenize content into words while preserving whitespace
  const tokenize = (text) => {
    const tokens = [];
    const regex = /(\S+|\s+)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      tokens.push(match[0]);
    }
    return tokens;
  };

  const originalTokens = tokenize(original);
  const improvedTokens = tokenize(improved);
  
  // Create LCS table
  const m = originalTokens.length;
  const n = improvedTokens.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  // Fill LCS table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (originalTokens[i - 1] === improvedTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Backtrack to find the actual diff
  const segments = [];
  let i = m, j = n;
  const result = [];
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalTokens[i - 1] === improvedTokens[j - 1]) {
      // Common token
      result.unshift({ text: improvedTokens[j - 1], isAdded: false });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      // Added token
      result.unshift({ text: improvedTokens[j - 1], isAdded: true });
      j--;
    } else {
      // Deleted token (we skip it)
      i--;
    }
  }
  
  // Merge consecutive segments with same status
  let currentSegment = null;
  for (const item of result) {
    if (currentSegment && currentSegment.isAdded === item.isAdded) {
      currentSegment.text += item.text;
    } else {
      if (currentSegment) {
        segments.push(currentSegment);
      }
      currentSegment = { text: item.text, isAdded: item.isAdded };
    }
  }
  if (currentSegment) {
    segments.push(currentSegment);
  }
  
  const hasChanges = segments.some(seg => seg.isAdded);
  
  return {
    segments,
    hasChanges
  };
};

/**
 * Find Longest Common Subsequence between two arrays
 * @param {array} arr1 - First array
 * @param {array} arr2 - Second array
 * @returns {array} LCS array
 */
const findLCS = (arr1, arr2) => {
  const m = arr1.length;
  const n = arr2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  // Build the LCS matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (arr1[i - 1] === arr2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Reconstruct the LCS
  const lcs = [];
  let i = m, j = n;
  
  while (i > 0 && j > 0) {
    if (arr1[i - 1] === arr2[j - 1]) {
      lcs.unshift(arr1[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  
  return lcs;
};

/**
 * Advanced content comparison using line-by-line diff
 * This provides better results for markdown content
 * @param {string} original - Original content
 * @param {string} improved - Improved content
 * @returns {object} Object containing marked content with additions highlighted
 */
export const compareContentByLines = (original, improved) => {
  if (!original || !improved) {
    return {
      markedContent: improved || '',
      hasChanges: original !== improved,
      improvedLines: []
    };
  }

  const originalLines = original.split('\n');
  const improvedLines = improved.split('\n');
  
  const lcs = findLCS(originalLines, improvedLines);
  const markedLines = [];
  const addedLineIndices = [];
  let lcsIndex = 0;
  let hasChanges = false;
  
  for (let i = 0; i < improvedLines.length; i++) {
    const line = improvedLines[i];
    
    if (lcsIndex < lcs.length && line === lcs[lcsIndex]) {
      // Line exists in original
      markedLines.push(line);
      lcsIndex++;
    } else {
      // Line is new or modified
      hasChanges = true;
      
      if (line.trim() === '') {
        // Empty lines don't need marking
        markedLines.push(line);
      } else {
        // Mark this line index as added
        addedLineIndices.push(i);
        markedLines.push(line);
      }
    }
  }
  
  return {
    markedContent: markedLines.join('\n'),
    hasChanges,
    addedLineIndices
  };
};